/**
 * WhatIfPanel Component
 *
 * Interactive panel for breakeven scenario modelling:
 * - Fixed costs adjustment slider
 * - Price adjustment slider
 * - Strain exclusion checkboxes
 * - Calculate and reset buttons
 * - Scenario results display
 *
 * Part of US-P7-003: Breakeven Analysis
 */

import { useState, useCallback } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Slider } from '@/components/ui/slider'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import {
  HelpCircle,
  Calculator,
  RotateCcw,
  TrendingUp,
  TrendingDown,
  Minus
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

/**
 * Format currency
 */
function formatCurrency(value) {
  if (value === null || value === undefined) return 'N/A'
  const sign = value >= 0 ? '+' : ''
  return `${sign}${new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)}`
}

/**
 * Format percentage change
 */
function formatPercentChange(value) {
  if (value === null || value === undefined) return 'N/A'
  const sign = value > 0 ? '+' : ''
  return `${sign}${value.toFixed(1)}%`
}

/**
 * Format kg
 */
function formatKg(value) {
  if (value === null || value === undefined) return 'N/A'
  if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toFixed(1)}t`
  }
  return `${value.toFixed(1)} kg`
}

/**
 * Get trend icon and colour
 */
function getTrendIndicator(change, higherIsBetter = false) {
  if (change === null || change === undefined) {
    return { icon: Minus, colour: 'text-neutral-400' }
  }

  const isPositive = higherIsBetter ? change > 0 : change < 0

  if (Math.abs(change) < 0.1) {
    return { icon: Minus, colour: 'text-neutral-400' }
  }

  if (isPositive) {
    return { icon: TrendingUp, colour: 'text-emerald-600' }
  }

  return { icon: TrendingDown, colour: 'text-red-600' }
}

/**
 * Scenario Result Item
 */
function ResultItem({ label, value, change, changeLabel, higherIsBetter = false }) {
  const { icon: TrendIcon, colour } = getTrendIndicator(change, higherIsBetter)

  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-neutral-600">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-medium">{value}</span>
        {change !== null && change !== undefined && (
          <span className={`flex items-center gap-1 text-sm ${colour}`}>
            <TrendIcon className="h-4 w-4" />
            {changeLabel || formatPercentChange(change)}
          </span>
        )}
      </div>
    </div>
  )
}

/**
 * WhatIfPanel Component
 *
 * @param {Object} props
 * @param {Array} props.strains - Available strains for exclusion
 * @param {Object} props.adjustments - Current adjustment values
 * @param {Function} props.setFixedCostsAdjustment - Fixed costs setter
 * @param {Function} props.setPriceAdjustmentPercent - Price adjustment setter
 * @param {Function} props.toggleStrainExclusion - Strain toggle function
 * @param {Function} props.calculate - Calculate scenario function
 * @param {Function} props.reset - Reset function
 * @param {Object} props.scenario - Calculated scenario data
 * @param {Object} props.comparison - Comparison with baseline
 * @param {boolean} props.isCalculating - Loading state
 * @param {number} props.baselineFixedCosts - Current fixed costs for reference
 * @param {string} [props.className] - Additional CSS classes
 */
export default function WhatIfPanel({
  strains = [],
  adjustments = {},
  setFixedCostsAdjustment,
  setPriceAdjustmentPercent,
  toggleStrainExclusion,
  calculate,
  reset,
  scenario,
  comparison,
  isCalculating = false,
  baselineFixedCosts = 0,
  className = ''
}) {
  // Local state for input values (allows typing before committing)
  const [localFixedCosts, setLocalFixedCosts] = useState(adjustments.fixedCostsAdjustment || 0)
  const [localPricePercent, setLocalPricePercent] = useState(adjustments.priceAdjustmentPercent || 0)

  // Update local state when adjustments prop changes
  const handleFixedCostsSliderChange = useCallback((values) => {
    const value = values[0]
    setLocalFixedCosts(value)
    setFixedCostsAdjustment(value)
  }, [setFixedCostsAdjustment])

  const handlePriceSliderChange = useCallback((values) => {
    const value = values[0]
    setLocalPricePercent(value)
    setPriceAdjustmentPercent(value)
  }, [setPriceAdjustmentPercent])

  const handleReset = useCallback(() => {
    setLocalFixedCosts(0)
    setLocalPricePercent(0)
    reset()
  }, [reset])

  const excludedStrains = adjustments.excludeStrains || []

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          What-If Scenarios
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-neutral-400" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>
                  Model how changes to costs or pricing would affect your breakeven point.
                  Adjust the sliders and click Calculate to see the impact.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Fixed Costs Adjustment */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-neutral-700">
              Fixed Costs Adjustment
            </label>
            <span className="text-sm font-medium text-neutral-900">
              {formatCurrency(localFixedCosts)}
            </span>
          </div>
          <Slider
            value={[localFixedCosts]}
            onValueChange={handleFixedCostsSliderChange}
            min={-Math.max(baselineFixedCosts * 0.5, 500)}
            max={Math.max(baselineFixedCosts * 0.5, 500)}
            step={50}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-neutral-500">
            <span>-{formatCurrency(Math.abs(Math.max(baselineFixedCosts * 0.5, 500))).slice(1)}</span>
            <span>{formatCurrency(Math.max(baselineFixedCosts * 0.5, 500)).slice(1)}</span>
          </div>
        </div>

        {/* Price Adjustment */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-neutral-700">
              Price Adjustment
            </label>
            <span className="text-sm font-medium text-neutral-900">
              {localPricePercent >= 0 ? '+' : ''}{localPricePercent}%
            </span>
          </div>
          <Slider
            value={[localPricePercent]}
            onValueChange={handlePriceSliderChange}
            min={-20}
            max={20}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-neutral-500">
            <span>-20%</span>
            <span>+20%</span>
          </div>
        </div>

        {/* Strain Exclusion */}
        {strains.length > 0 && (
          <div className="space-y-3">
            <label className="text-sm font-medium text-neutral-700">
              Exclude Strains
            </label>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {strains.map((strain) => (
                <div key={strain.id} className="flex items-center gap-2">
                  <Checkbox
                    id={`strain-${strain.id}`}
                    checked={excludedStrains.includes(strain.id)}
                    onCheckedChange={() => toggleStrainExclusion(strain.id)}
                  />
                  <label
                    htmlFor={`strain-${strain.id}`}
                    className="text-sm text-neutral-700 flex-1 cursor-pointer"
                  >
                    {strain.name}
                    <span className="text-xs text-neutral-500 ml-2">
                      ({formatKg(strain.kg_sold)})
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            onClick={() => calculate()}
            disabled={isCalculating}
            className="flex-1"
          >
            <Calculator className="h-4 w-4 mr-2" />
            {isCalculating ? 'Calculating...' : 'Calculate'}
          </Button>
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={isCalculating}
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>

        {/* Scenario Results */}
        {scenario && (
          <div className="pt-4 border-t border-neutral-200 space-y-1">
            <h4 className="text-sm font-medium text-neutral-900 mb-3">
              Projected Impact
            </h4>

            <ResultItem
              label="Breakeven (kg)"
              value={formatKg(scenario.breakeven_kg)}
              change={comparison?.breakeven_kg_change}
              changeLabel={comparison?.breakeven_kg_change !== null
                ? `${comparison.breakeven_kg_change >= 0 ? '+' : ''}${formatKg(comparison.breakeven_kg_change)}`
                : null
              }
              higherIsBetter={false}
            />

            <ResultItem
              label="Safety Margin"
              value={`${scenario.safety_margin_percent?.toFixed(1) ?? 'N/A'}%`}
              change={comparison?.safety_margin_change}
              higherIsBetter={true}
            />

            <ResultItem
              label="Performance"
              value={`${scenario.performance_percent?.toFixed(1) ?? 'N/A'}%`}
              change={null}
              higherIsBetter={true}
            />

            <div className="flex items-center justify-between py-2 border-t border-neutral-100 mt-2">
              <span className="text-sm text-neutral-600">Fixed Costs</span>
              <span className="font-medium">
                {new Intl.NumberFormat('en-GB', {
                  style: 'currency',
                  currency: 'GBP',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }).format(scenario.fixed_costs)}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * WhatIfPanel Skeleton
 */
export function WhatIfPanelSkeleton({ className = '' }) {
  return (
    <Card className={className}>
      <CardHeader>
        <Skeleton className="h-6 w-40" />
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-full" />
        </div>
        <div className="space-y-3">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-full" />
        </div>
        <div className="space-y-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
        </div>
        <Skeleton className="h-10 w-full" />
      </CardContent>
    </Card>
  )
}
